import axios, { AxiosRequestConfig, AxiosError } from 'axios'
import { getCookie, getCsrfToken } from '@share/utils/index'
import zhHK from '@yuewen/reader/locales/zhHK'
import { PageContextServer, XOR } from '@root/types'
import { MyResponseType, PolarsType } from '@share/types'
import JSONBig from 'json-bigint'
import { useToast } from '@yuewen/reader'
import { isBlob } from '@share/utils/is'
import { login as pcLogin } from '@root/packages/pc/src/utils/login'
import { login as mLogin } from '@root/packages/m/src/utils/login'

interface IAxiosRequestConfigParams {
  [key: string]: unknown
  _csrfToken: string
}
interface IAxiosRequestConfigData {
  [key: string]: unknown
  _csrfToken: string
}

const serverTime = 0

export const axiosInstance = axios.create({
  timeout: 8000, // 8秒超时
  xsrfCookieName: '',
  xsrfHeaderName: '',
  validateStatus: null, // 非200不报错
  maxRedirects: 0, // 不重定向
  withCredentials: true,
})

axiosInstance.interceptors.request.use(
  (
    config: AxiosRequestConfig<XOR<IAxiosRequestConfigData, URLSearchParams>>,
  ) => {
    if (!import.meta.env.SSR) {
      setCustomHeadersAndParams(config)
    }
    const params = config.params as IAxiosRequestConfigParams
    config.transformResponse = [
      (data: string): unknown => {
        let cookie = ''
        let transData = data

        if (import.meta.env.SSR) {
          cookie = config.headers
            ? (config?.headers.Cookie as string) || ''
            : ''
        }
        if (
          getCookie('lang', cookie) === 'zht' &&
          import.meta.env.VITE_PLATFORM === 'PC' &&
          !params?.disableZhHK &&
          !config?.data?.disableZhHK
        ) {
          transData = zhHK._trans.trans(data)
        }
        if (import.meta.env.SSR && transData) {
          return JSONBig.parse(transData)
        }
        return isBlob(transData) || !transData ? data : JSONBig.parse(transData)
      },
    ]
    return config
  },
)

axiosInstance.interceptors.response.use(
  (res) => {
    if (res.status === 401) {
      if (import.meta.env.VITE_PLATFORM === 'PC') {
        pcLogin()
      }
      if (import.meta.env.VITE_PLATFORM === 'M') {
        mLogin()
      }
      throw new Error('无权限')
    }
    return res
  },
  (error) => {
    if (import.meta.env.SSR) {
      throw error
    }
    if (!import.meta.env.SSR) {
      useToast({
        title: '网络异常，刷新重试',
        type: 'error',
      })
      // eslint-disable-next-line no-console
      console.log(error)
      dealWaf(error)
      return Promise.resolve(error)
    }
    return Promise.reject(error)
  },
)
// 状态(0:成功; 1:异常; 2:超时)
const dealStatus = (status: number): string => {
  if ([200, 301, 302, 406].includes(status)) {
    return '0'
  } else if (status === 504) {
    return '2'
  }
  return '1'
}

// eslint-disable-next-line max-statements
export const cgiRequest = async <T>(
  pageContext: PageContextServer<unknown, unknown>,
  url: string,
  params: unknown,
  cgiServerInfo: {
    cgiTafName?: string
    cgiPolaris?: PolarsType
    cgiIp?: string
  },
  notThrowError?: boolean,
): Promise<MyResponseType<T>> => {
  let curHost = ''
  const startTime = Date.now()
  const urlSearchParams = new URLSearchParams(params as Record<string, string>)
  if (import.meta.env.VITE_ENV_TYPE === 'local') {
    curHost = `${import.meta.env.VITE_CGIAPI_LOCAL_HOST}`
  } else {
    try {
      if (cgiServerInfo.cgiIp) {
        curHost = `${cgiServerInfo.cgiIp}`
      }
      if (cgiServerInfo.cgiTafName) {
        curHost = await pageContext.getAddrForTafName(
          cgiServerInfo.cgiTafName,
          pageContext,
        )
      }
      if (cgiServerInfo.cgiPolaris) {
        curHost = await pageContext.getAddrForPolaris(
          cgiServerInfo.cgiPolaris,
          pageContext,
        )
      }
    } catch (error) {
      pageContext.appendLog(`get cgi host error ${error as string}`)
    }
  }
  if (url.includes('http')) {
    pageContext.appendLog(`sendReq: ${url}?${urlSearchParams.toString()}`)
  } else {
    pageContext.appendLog(
      `sendReq: http://${curHost}${url}?${urlSearchParams.toString()}`,
    )
  }

  const baseActiveMsgTimeout = {
    aService: `${pageContext.serverInfo?.service.name}`, // 主调Service
    aInterface: `${pageContext?.urlOriginal}`, // 主调Interface
    pApp: '', // 被调App
    pServer:
      cgiServerInfo.cgiTafName || cgiServerInfo.cgiPolaris?.SERVICE || '', // 被调Server
    pService:
      cgiServerInfo.cgiTafName || cgiServerInfo.cgiPolaris?.SERVICE || '', // 被调Service
    pInterface: url, // 被调Interface
    pIp: curHost, // 被调IP
    pContainer: '', // 被调容器名
    pConSetId: '', // 被调容器的SetID
  }
  try {
    if (!curHost) {
      const noHostError = new Error() as AxiosError
      noHostError.code = 'GET IP ERROR'
      throw noHostError
    }
    const result = await axiosInstance.request<MyResponseType<T>>({
      method: 'GET',
      url: url.includes('http')
        ? url
        : `http${
            import.meta.env.VITE_ENV_TYPE === 'local' ? 's' : ''
          }://${curHost}${url}`,
      headers: {
        Cookie: pageContext.headers.cookie || '',
        host:
          import.meta.env.VITE_ENV_TYPE === 'local'
            ? ''
            : pageContext.headers.host,
        'x-forwarded-for': pageContext.headers['x-forwarded-for'] || '',
        'x-real-ip': pageContext.headers['x-real-ip'] || '',
      },
      params,
    })
    pageContext.appendLog(
      `x-forwarded-for === ${pageContext.headers['x-forwarded-for']},
       x-real-ip === ${pageContext.headers['x-real-ip']}`,
    )
    if (result.headers['set-cookie'] && result.headers['set-cookie'].length) {
      if (
        pageContext.headers['set-cookie'] &&
        pageContext.headers['set-cookie'].length
      ) {
        pageContext.headers['set-cookie'] = [
          ...pageContext.headers['set-cookie'],
          ...result.headers['set-cookie'],
        ]
      } else {
        pageContext.headers['set-cookie'] = result.headers['set-cookie']
      }
    }
    const spentTime = Date.now() - startTime
    pageContext.appendLog(
      `gotRes http://${curHost}${url}?${urlSearchParams.toString()} $ ${
        result.status
      }  $ ${spentTime / 1000}s`,
      [200, 301, 302].includes(result.status) ? 'Trace' : 'Error',
    )
    const activeMsg = {
      ...baseActiveMsgTimeout,
      retCode:
        result && result.data
          ? `${
              result?.data.code || result.data.code === 0
                ? result.data.code
                : `status: ${result.status} `
            }`
          : `status: ${result.status}`, // 业务返回码
      status: dealStatus(result.status),
      time: `${result.status === 200 ? spentTime : 0}`, // 成功时耗时(异常、超时时该值设置为0)
    }
    import.meta.env.VITE_ENV_TYPE !== 'local' &&
      pageContext.metricReport &&
      pageContext.metricReport.reportActive(activeMsg)
    pageContext.statusCode = result.status
    if (result.status === 301 || result.status === 302) {
      // 矩阵站不再需要为 qdmm（女频）场景重写 returnurl，直接透传后端 location
      const location = result?.headers?.location
      pageContext.redirectUrl = location
      pageContext.appendLog(
        `status: ${result.status},msg: redirect: ${location}`,
      )
      throw new Error()
    }
    const body = result.data
    if (body?.code !== 0 && !notThrowError) {
      pageContext.statusCode = 404
      pageContext.appendLog(
        `status: 404,msg:code: ${body.code}, msg: ${body?.msg || ''}`,
      )
      throw new Error()
    }
    return body
  } catch (err) {
    // 错误处理重定向 301 302
    if (pageContext.statusCode && [301, 302].includes(pageContext.statusCode)) {
      // eslint-disable-next-line new-cap
      throw pageContext.renderErrorPage({
        pageContext: {
          statusCode: pageContext.statusCode,
          redirectUrl: pageContext.redirectUrl,
        },
      })
    }
    // 错误处理 404（服务端非0错误码返回404）
    if (pageContext?.statusCode === 404) {
      // eslint-disable-next-line new-cap
      throw pageContext.renderErrorPage({
        pageContext: {
          statusCode: pageContext?.statusCode,
          pageTrackReportExt: pageContext.pageTrackReportExt,
        },
      })
    }
    pageContext.appendLog(
      `======= axios request error ========(error.code),${url}${
        (err as AxiosError)?.code as string
      }`,
    )
    pageContext.appendLog(err)
    // 错误处理 超时504
    const activeMsgTimeout = {
      ...baseActiveMsgTimeout,
      retCode: `${(err as AxiosError)?.code as string}`, // 业务返回码
      status: `${
        ((err as AxiosError)?.code as string) === 'ETIMEDOUT' ||
        (err as AxiosError)?.code === 'ECONNABORTED'
          ? '2'
          : '1'
      }`, // 状态(0:成功; 1:异常; 2:超时)
      time: '0', // 成功时耗时(异常、超时时该值设置为0)
    }
    import.meta.env.VITE_ENV_TYPE !== 'local' &&
      pageContext.metricReport &&
      pageContext.metricReport.reportActive(activeMsgTimeout)

    if (
      (err as AxiosError)?.code === 'ETIMEDOUT' ||
      (err as AxiosError)?.code === 'ECONNABORTED'
    ) {
      pageContext.statusCode = 504
    }
    if ((err as AxiosError).code === 'ECONNREFUSED') {
      pageContext.statusCode = 502
    }
    if (notThrowError) {
      return Promise.resolve({}) as Promise<MyResponseType<T>>
    }
    // eslint-disable-next-line new-cap
    throw pageContext.renderErrorPage({
      pageContext: {
        statusCode: pageContext.statusCode,
      },
    })
  }
}

/**
 * ajax请求头部处理
 * @param config
 * @returns
 */
export const setCustomHeadersAndParams = (
  config: AxiosRequestConfig<
    IAxiosRequestConfigData | URLSearchParams | FormData
  >,
): void => {
  const csrfToken = getCsrfToken()
  const currentTime = new Date().getTime() / 1000

  const _timeDistance = serverTime ? serverTime - currentTime : 0

  let currentTimestamp = Math.floor(new Date().getTime() / 1000)
  currentTimestamp += _timeDistance
  const signValue = window.Fock?.sign(`${currentTimestamp}${csrfToken}`)
  config.headers = {
    ...config.headers,
    'X-Yuew-time': currentTimestamp,
    'X-Yuew-sign': signValue,
    'X-D': _timeDistance,
  }
  if (config.method === 'post') {
    const data = config.data || {}
    if (typeof data === 'string') {
      return
    }
    if (
      config.headers['Content-type'] === 'application/x-www-form-urlencoded'
    ) {
      const form = new FormData()
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          form.append(key, (data as { [key: string]: string })[key])
        }
      }
      form.append('_csrfToken', csrfToken)

      config.data = form
    } else {
      config.data = {
        ...data,
        _csrfToken: csrfToken,
      }
    }
  }
  const params = (config.params || {}) as IAxiosRequestConfigParams
  if (!params._csrfToken && config.url?.indexOf('csrfToken') === -1) {
    config.params = {
      ...params,
      _csrfToken: csrfToken,
    }
  }
}

export const dealWaf = (error: { text: string } & AxiosError): void => {
  // WAF 验证码seqid相关正则
  const data = error.text
  const sig_data = /seqid\s=\s"(\w+)"/g
  const waf_id_data = /TencentCaptcha\(\'(\d+)\'/g
  // 捕捉错误及渲染验证码
  const matches = sig_data.exec(data)
  if (matches) {
    // 展示验证码
    const seqid = matches[1]
    const wid_matches = waf_id_data.exec(data)
    const wid = wid_matches ? wid_matches[1] : 0
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const captcha = new TencentCaptcha(
      wid,
      (captchaData: { ret: number; ticket: string; randstr: string }) => {
        const captchaResult = []
        captchaResult.push(captchaData.ret)
        if (captchaData.ret === 0) {
          captchaResult.push(captchaData.ticket)
          captchaResult.push(captchaData.randstr)
          captchaResult.push(seqid)
        }
        const content = captchaResult.join('\n')
        // eslint-disable-next-line no-void
        void axios
          .post('/WafCaptcha', content)
          .then(() => {
            window.location.reload()
          })
          .catch(() => {
            window.location.reload()
          })
      },
    )
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    captcha.show()
  } else {
    useToast({
      title: '网络异常，刷新重试',
      type: 'error',
    })
  }
}
